import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MarkdownEditor from './MarkdownEditor';
import MarkdownContent from './MarkdownContent';
import ImageCropModal from './ImageCropModal';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const toDateTimeLocalValue = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getNowDateTimeLocalValue = () => toDateTimeLocalValue(new Date());

const AdminPanel = () => {
  const [entries, setEntries] = useState([]);
  const [hobbyEntries, setHobbyEntries] = useState([]);
  const [aboutMe, setAboutMe] = useState('');
  const [profile, setProfile] = useState({
    name: '',
    tagline: '',
    email: '',
    linkedin_url: '',
    github_url: '',
    hackster_url: ''
  });
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [profileCoverUrl, setProfileCoverUrl] = useState(null);
  const [coverCropSrc, setCoverCropSrc] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showHobbyModal, setShowHobbyModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingHobbyEntry, setEditingHobbyEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({ title: '', content: '', created_at: '' });
  const [hobbyForm, setHobbyForm] = useState({ title: '', content: '', created_at: '' });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [contentImageUploading, setContentImageUploading] = useState(false);
  const [photoCropSrc, setPhotoCropSrc] = useState(null);
  const photoInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        handleLogout();
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/auth/validate`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.valid) {
          fetchEntries();
          fetchHobbyEntries();
          fetchAboutMe();
          fetchProfile();
        } else {
          handleLogout();
        }
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          handleLogout();
        } else {
          handleLogout();
        }
      }
    };
    validateToken();
  }, [navigate, location.pathname]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/entries`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error fetching entries:', err);
      if (err.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const fetchHobbyEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/hobbies`);
      setHobbyEntries(response.data);
    } catch (err) {
      console.error('Error fetching hobby entries:', err);
      if (err.response?.status === 401) handleLogout();
    }
  };

  const fetchAboutMe = async () => {
    try {
      const response = await axios.get(`${API_URL}/about`);
      setAboutMe(response.data.content || '');
    } catch (err) {
      console.error('Error fetching about me:', err);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile`);
      const p = response.data;
      setProfile({
        name: p.name || '',
        tagline: p.tagline || '',
        email: p.email || '',
        linkedin_url: p.linkedin_url || '',
        github_url: p.github_url || '',
        hackster_url: p.hackster_url || ''
      });
      setProfileImageUrl(p.image_url || null);
      setProfileCoverUrl(p.cover_url || null);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    navigate('/admin/login');
  };

  const handleCreateEntry = () => {
    setEditingEntry(null);
    setEntryForm({ title: '', content: '', created_at: getNowDateTimeLocalValue() });
    setShowEntryModal(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      title: entry.title,
      content: entry.content,
      created_at: toDateTimeLocalValue(entry.created_at)
    });
    setShowEntryModal(true);
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/entries/${editingEntry.id}`, entryForm, { headers: getAuthHeaders() });
        setSuccess('Entry updated successfully!');
      } else {
        await axios.post(`${API_URL}/entries`, entryForm, { headers: getAuthHeaders() });
        setSuccess('Entry created successfully!');
      }
      setShowEntryModal(false);
      fetchEntries();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save entry');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/entries/${id}`, { headers: getAuthHeaders() });
      setSuccess('Entry deleted successfully!');
      fetchEntries();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete entry');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const handleCreateHobbyEntry = () => {
    setEditingHobbyEntry(null);
    setHobbyForm({ title: '', content: '', created_at: getNowDateTimeLocalValue() });
    setShowHobbyModal(true);
  };

  const handleEditHobbyEntry = (entry) => {
    setEditingHobbyEntry(entry);
    setHobbyForm({
      title: entry.title,
      content: entry.content,
      created_at: toDateTimeLocalValue(entry.created_at)
    });
    setShowHobbyModal(true);
  };

  const handleSaveHobbyEntry = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingHobbyEntry) {
        await axios.put(`${API_URL}/hobbies/${editingHobbyEntry.id}`, hobbyForm, { headers: getAuthHeaders() });
        setSuccess('Hobby entry updated successfully!');
      } else {
        await axios.post(`${API_URL}/hobbies`, hobbyForm, { headers: getAuthHeaders() });
        setSuccess('Hobby entry created successfully!');
      }
      setShowHobbyModal(false);
      fetchHobbyEntries();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save hobby entry');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const handleDeleteHobbyEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hobby entry?')) return;
    try {
      await axios.delete(`${API_URL}/hobbies/${id}`, { headers: getAuthHeaders() });
      setSuccess('Hobby entry deleted successfully!');
      fetchHobbyEntries();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete hobby entry');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const handleSaveAboutMe = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/about`, { content: aboutMe }, { headers: getAuthHeaders() });
      setSuccess('About me updated successfully!');
      setShowAboutModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update about me');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const openProfileModal = () => {
    fetchProfile();
    setShowProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/profile`, profile, { headers: getAuthHeaders() });
      setSuccess('Profile updated successfully!');
      setShowProfileModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
      if (err.response?.status === 401) handleLogout();
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setPhotoCropSrc(src);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handlePhotoCropComplete = async (blob) => {
    if (!photoCropSrc) return;
    setPhotoUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('photo', blob, 'photo.jpg');
    try {
      const response = await axios.post(`${API_URL}/profile/photo`, formData, {
        headers: getAuthHeaders()
      });
      setProfileImageUrl(response.data.image_url);
      setSuccess('Photo updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setPhotoUploading(false);
      URL.revokeObjectURL(photoCropSrc);
      setPhotoCropSrc(null);
    }
  };

  const handlePhotoCropCancel = () => {
    if (photoCropSrc) URL.revokeObjectURL(photoCropSrc);
    setPhotoCropSrc(null);
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverCropSrc(URL.createObjectURL(file));
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleCoverCropComplete = async (blob) => {
    if (!coverCropSrc) return;
    setCoverUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('cover', blob, 'cover.jpg');
    try {
      const response = await axios.post(`${API_URL}/profile/cover`, formData, {
        headers: getAuthHeaders()
      });
      setProfileCoverUrl(response.data.cover_url);
      setSuccess('Cover image updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload cover');
    } finally {
      setCoverUploading(false);
      URL.revokeObjectURL(coverCropSrc);
      setCoverCropSrc(null);
    }
  };

  const handleCoverCropCancel = () => {
    if (coverCropSrc) URL.revokeObjectURL(coverCropSrc);
    setCoverCropSrc(null);
  };

  const handleRemoveCover = async () => {
    if (!profileCoverUrl) return;
    if (!window.confirm('Remove cover image?')) return;
    setCoverUploading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/profile/cover`, { headers: getAuthHeaders() });
      setProfileCoverUrl(null);
      setSuccess('Cover removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove cover');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleUploadContentImage = async (fileOrBlob) => {
    setContentImageUploading(true);
    const formData = new FormData();
    const name = fileOrBlob instanceof File ? fileOrBlob.name : 'image.jpg';
    formData.append('image', fileOrBlob, name);
    try {
      const response = await axios.post(`${API_URL}/uploads/content`, formData, {
        headers: getAuthHeaders()
      });
      return response.data.url;
    } finally {
      setContentImageUploading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const username = localStorage.getItem('username');

  return (
    <div className="admin-panel">
      <div className="header">
        <div className="admin-header">
          <div>
            <h2>Admin Panel</h2>
            <p className="admin-welcome">Welcome, {username}!</p>
          </div>
          <div className="admin-header-actions">
            <a href="/" className="btn btn-secondary">View Journal</a>
            <button onClick={handleLogout} className="btn btn-secondary">Logout</button>
          </div>
        </div>
      </div>

      <div className="container">
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <div className="admin-actions">
          <button onClick={handleCreateEntry} className="btn btn-primary">+ New Entry</button>
          <button onClick={handleCreateHobbyEntry} className="btn btn-primary">+ New Hobby Entry</button>
          <button onClick={openProfileModal} className="btn btn-primary">Edit Profile</button>
          <button onClick={() => setShowAboutModal(true)} className="btn btn-primary">Edit About Me</button>
        </div>

        <h3 className="admin-section-title">Journal Entries</h3>
        <div className="entries-list">
          {entries.length === 0 ? (
            <div className="empty-state card">
              <h3>No entries yet</h3>
              <p>Create your first journal entry!</p>
            </div>
          ) : (
            entries.map((entry) => (
              <div key={entry.id} className="card entry-card">
                <div className="entry-header">
                  <div>
                    <h3 className="entry-title">{entry.title}</h3>
                    <div className="entry-date">{formatDate(entry.created_at)}</div>
                  </div>
                  <div className="entry-actions">
                    <button onClick={() => handleEditEntry(entry)} className="btn btn-secondary btn-sm">Edit</button>
                    <button onClick={() => handleDeleteEntry(entry.id)} className="btn btn-danger btn-sm">Delete</button>
                  </div>
                </div>
                <div className="entry-content"><MarkdownContent content={entry.content} /></div>
              </div>
            ))
          )}
        </div>

        <h3 className="admin-section-title">Hobbies &amp; Interests</h3>
        <div className="entries-list">
          {hobbyEntries.length === 0 ? (
            <div className="empty-state card">
              <h3>No hobby entries yet</h3>
              <p>Add your first hobby or interest entry!</p>
            </div>
          ) : (
            hobbyEntries.map((entry) => (
              <div key={entry.id} className="card entry-card">
                <div className="entry-header">
                  <div>
                    <h3 className="entry-title">{entry.title}</h3>
                    <div className="entry-date">{formatDate(entry.created_at)}</div>
                  </div>
                  <div className="entry-actions">
                    <button onClick={() => handleEditHobbyEntry(entry)} className="btn btn-secondary btn-sm">Edit</button>
                    <button onClick={() => handleDeleteHobbyEntry(entry.id)} className="btn btn-danger btn-sm">Delete</button>
                  </div>
                </div>
                <div className="entry-content"><MarkdownContent content={entry.content} /></div>
              </div>
            ))
          )}
        </div>
      </div>

      {showEntryModal && (
        <div className="modal" onClick={() => setShowEntryModal(false)}>
          <div className="modal-content modal-content-editor" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingEntry ? 'Edit Entry' : 'New Entry'}</h3>
              <button type="button" className="close-btn" onClick={() => setShowEntryModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveEntry}>
              <div className="input-group">
                <label>Title</label>
                <input type="text" value={entryForm.title} onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })} required />
              </div>
              <div className="input-group">
                <label>Publish Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={entryForm.created_at}
                  onChange={(e) => setEntryForm({ ...entryForm, created_at: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label>Content (Markdown: tables, images, links)</label>
                <MarkdownEditor
                  value={entryForm.content}
                  onChange={(v) => setEntryForm({ ...entryForm, content: v })}
                  minHeight={430}
                  onUploadImage={handleUploadContentImage}
                  uploadDisabled={contentImageUploading}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEntryModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingEntry ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHobbyModal && (
        <div className="modal" onClick={() => setShowHobbyModal(false)}>
          <div className="modal-content modal-content-editor" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingHobbyEntry ? 'Edit Hobby Entry' : 'New Hobby Entry'}</h3>
              <button type="button" className="close-btn" onClick={() => setShowHobbyModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveHobbyEntry}>
              <div className="input-group">
                <label>Title</label>
                <input type="text" value={hobbyForm.title} onChange={(e) => setHobbyForm({ ...hobbyForm, title: e.target.value })} required placeholder="e.g. Photography" />
              </div>
              <div className="input-group">
                <label>Publish Date &amp; Time</label>
                <input
                  type="datetime-local"
                  value={hobbyForm.created_at}
                  onChange={(e) => setHobbyForm({ ...hobbyForm, created_at: e.target.value })}
                  required
                />
              </div>
              <div className="input-group">
                <label>Content (Markdown: tables, images, links)</label>
                <MarkdownEditor
                  value={hobbyForm.content}
                  onChange={(v) => setHobbyForm({ ...hobbyForm, content: v })}
                  minHeight={430}
                  onUploadImage={handleUploadContentImage}
                  uploadDisabled={contentImageUploading}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowHobbyModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingHobbyEntry ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAboutModal && (
        <div className="modal" onClick={() => setShowAboutModal(false)}>
          <div className="modal-content modal-content-editor" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit About Me</h3>
              <button type="button" className="close-btn" onClick={() => setShowAboutModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveAboutMe}>
              <div className="input-group">
                <label>Content (Markdown: tables, images, links)</label>
                <MarkdownEditor
                  value={aboutMe}
                  onChange={setAboutMe}
                  minHeight={560}
                  onUploadImage={handleUploadContentImage}
                  uploadDisabled={contentImageUploading}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAboutModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="modal" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Profile</h3>
              <button type="button" className="close-btn" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <form onSubmit={handleSaveProfile}>
              <div className="profile-cover-upload">
                <label>Cover / background image</label>
                <p className="field-hint">Horizontal banner behind your profile (e.g. 2.5:1 ratio)</p>
                {profileCoverUrl ? (
                  <div className="cover-preview-wrap">
                    <img
                      src={profileCoverUrl.startsWith('http') ? profileCoverUrl : (API_URL.startsWith('http') ? API_URL.replace(/\/api\/?$/, '') : window.location.origin) + profileCoverUrl}
                      alt="Cover"
                      className="cover-preview"
                    />
                    <div className="cover-preview-actions">
                      <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleCoverChange} className="input-file" />
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => coverInputRef.current?.click()}>Change</button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={handleRemoveCover} disabled={coverUploading}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <input ref={coverInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleCoverChange} className="input-file" style={{ display: 'none' }} />
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => coverInputRef.current?.click()}>Choose image</button>
                    {coverUploading && <p className="upload-status">Uploading…</p>}
                  </div>
                )}
                {coverCropSrc && (
                  <ImageCropModal
                    imageSrc={coverCropSrc}
                    aspect={2.5}
                    onComplete={handleCoverCropComplete}
                    onCancel={handleCoverCropCancel}
                  />
                )}
              </div>
              <div className="profile-photo-upload">
                <label>Profile Photo</label>
                <div className="profile-photo-preview-wrap">
                  {profileImageUrl ? (
                    <img src={profileImageUrl.startsWith('http') ? profileImageUrl : (API_URL.startsWith('http') ? API_URL.replace(/\/api\/?$/, '') : window.location.origin) + profileImageUrl} alt="Profile" className="profile-photo-preview" />
                  ) : (
                    <div className="profile-photo-preview placeholder">No photo</div>
                  )}
                </div>
                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handlePhotoChange} className="input-file" />
                {photoUploading && <p className="upload-status">Uploading…</p>}
                {photoCropSrc && (
                  <ImageCropModal
                    imageSrc={photoCropSrc}
                    aspect={1}
                    onComplete={handlePhotoCropComplete}
                    onCancel={handlePhotoCropCancel}
                  />
                )}
              </div>
              <div className="input-group">
                <label>Name</label>
                <input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Your full name" />
              </div>
              <div className="input-group">
                <label>Tagline</label>
                <input type="text" value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} placeholder="e.g. Software Engineer | Maker" />
              </div>
              <div className="input-group">
                <label>Email</label>
                <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="your@email.com" />
              </div>
              <div className="input-group">
                <label>LinkedIn URL</label>
                <input type="url" value={profile.linkedin_url} onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/..." />
              </div>
              <div className="input-group">
                <label>GitHub URL</label>
                <input type="url" value={profile.github_url} onChange={(e) => setProfile({ ...profile, github_url: e.target.value })} placeholder="https://github.com/..." />
              </div>
              <div className="input-group">
                <label>Hackster.io URL</label>
                <input type="url" value={profile.hackster_url} onChange={(e) => setProfile({ ...profile, hackster_url: e.target.value })} placeholder="https://www.hackster.io/..." />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowProfileModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Profile</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
